import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";

import MySnackbar from "../../components/UI/MySnackBar";
import { RootCeleste } from "../../components/Root";
import { HomeViewTitle } from "../../styles/Titles";
import { editTutorProfile, getTutorDirectory } from "../../api/handleTutors";

const Root = RootCeleste;
const Title = HomeViewTitle;

// Mismos valores que los enums del backend (users/models.py). El value es lo
// que viaja por la API; el label es lo que se muestra.
export const POSITIONS = [
  { value: "profesor", label: "Profesor" },
  { value: "jtp", label: "JTP" },
  { value: "ayudante", label: "Ayudante" },
  { value: "otro", label: "Otro" },
];

export const DEPARTMENTS = [
  { value: "computacion", label: "Computación" },
  { value: "industrias", label: "Industrias" },
  { value: "quimica", label: "Química" },
  { value: "electronica", label: "Electrónica" },
  { value: "otros", label: "Otros" },
];

const labelOf = (options, value) =>
  options.find((o) => o.value === value)?.label ?? "";

const ALL = "__all__";

// "1C2024" -> [2024, 1] para ordenar cronologicamente
const periodKey = (id) => [parseInt(id.slice(2), 10), parseInt(id[0], 10)];
const comparePeriods = (a, b) => {
  const [ya, ta] = periodKey(a);
  const [yb, tb] = periodKey(b);
  return ya === yb ? ta - tb : ya - yb;
};

const EMPTY_FORM = { phone: "", position: "", department: "" };

const TutorDirectoryView = () => {
  const user = useSelector((state) => state.user);

  const [directory, setDirectory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState(ALL);
  const [departmentFilter, setDepartmentFilter] = useState(ALL);
  // null => modal cerrado
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    status: "",
  });
  const notify = (message, status) =>
    setNotification({ open: true, message, status });
  const handleSnackbarClose = () =>
    setNotification({ ...notification, open: false });

  const fetchDirectory = useCallback(async () => {
    try {
      setIsLoading(true);
      setDirectory(await getTutorDirectory(user));
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDirectory();
  }, [fetchDirectory]);

  // Una columna por cuatrimestre en el que alguien tutoreo, en orden.
  const periods = useMemo(() => {
    const ids = new Set();
    directory.forEach((t) => t.periods.forEach((p) => ids.add(p.period_id)));
    return Array.from(ids).sort(comparePeriods);
  }, [directory]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return directory.filter((t) => {
      if (positionFilter !== ALL && t.position !== positionFilter) return false;
      if (departmentFilter !== ALL && t.department !== departmentFilter)
        return false;
      if (!needle) return true;
      return `${t.name} ${t.last_name} ${t.email}`
        .toLowerCase()
        .includes(needle);
    });
  }, [directory, search, positionFilter, departmentFilter]);

  const openEditModal = (tutor) => {
    setForm({
      phone: tutor.phone ?? "",
      position: tutor.position ?? "",
      department: tutor.department ?? "",
    });
    setEditing(tutor);
  };

  const closeEditModal = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      await editTutorProfile(editing.id, form, user);
      notify("Ficha actualizada", "success");
      closeEditModal();
      await fetchDirectory();
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Celda de un cuatrimestre: dirigidos (y evaluados, si hubo) sobre capacidad.
  const renderPeriodCell = (tutor, periodId) => {
    const p = tutor.periods.find((x) => x.period_id === periodId);
    if (!p) {
      return (
        <TableCell key={periodId} align="center" sx={{ color: "#bbb" }}>
          –
        </TableCell>
      );
    }
    const detail = [
      `Capacidad declarada: ${p.capacity}`,
      `Grupos dirigidos: ${p.tutored_groups}`,
      `Grupos evaluados: ${p.evaluated_groups}`,
      p.is_evaluator ? "Fue evaluador" : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <TableCell key={periodId} align="center">
        <Tooltip title={detail}>
          <span>
            <strong>{p.tutored_groups}</strong>
            <span style={{ color: "#888" }}> / {p.capacity}</span>
            {p.evaluated_groups > 0 && (
              <span style={{ color: "#888" }}> (+{p.evaluated_groups} eval.)</span>
            )}
          </span>
        </Tooltip>
      </TableCell>
    );
  };

  return (
    <Root maxWidth="xl">
      <Title variant="h4">Directores</Title>
      <Typography variant="body1" style={{ color: "#555", marginBottom: 16 }}>
        Todos los tutores que pasaron por el sistema, con su ficha y cuántos
        grupos dirigieron en cada cuatrimestre. Cada celda muestra{" "}
        <strong>dirigidos / capacidad</strong>, y entre paréntesis los grupos
        que evaluó. El teléfono, el cargo y el departamento se editan acá o se
        cargan con las columnas opcionales del CSV de tutores.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          marginBottom: 2,
          alignItems: "center",
        }}
      >
        <TextField
          label="Buscar"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Nombre, apellido o mail"
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label="Cargo"
          value={positionFilter}
          onChange={(e) => setPositionFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value={ALL}>Todos</MenuItem>
          {POSITIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Departamento"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value={ALL}>Todos</MenuItem>
          {DEPARTMENTS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" style={{ color: "#888" }}>
          {rows.length} de {directory.length}
        </Typography>
      </Box>

      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ width: "100%" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Apellido</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Mail</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Teléfono</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Cargo</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Departamento</TableCell>
                {periods.map((p) => (
                  <TableCell key={p} sx={{ fontWeight: "bold" }} align="center">
                    {p}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Total
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((tutor) => (
                <TableRow key={tutor.id} hover>
                  <TableCell>
                    {tutor.last_name}
                    {tutor.role === "admin" && (
                      <Tooltip title="Administrador que además dirige grupos (responsable de la materia)">
                        <Chip
                          label="admin"
                          size="small"
                          sx={{ marginLeft: 1 }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{tutor.name}</TableCell>
                  <TableCell>{tutor.email}</TableCell>
                  <TableCell>{tutor.phone || ""}</TableCell>
                  <TableCell>{labelOf(POSITIONS, tutor.position)}</TableCell>
                  <TableCell>{labelOf(DEPARTMENTS, tutor.department)}</TableCell>
                  {periods.map((p) => renderPeriodCell(tutor, p))}
                  <TableCell align="center">
                    <strong>{tutor.total_tutored_groups}</strong>
                    {tutor.total_evaluated_groups > 0 && (
                      <span style={{ color: "#888" }}>
                        {" "}
                        (+{tutor.total_evaluated_groups} eval.)
                      </span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar ficha">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => openEditModal(tutor)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8 + periods.length} align="center">
                    <Typography variant="body2" style={{ color: "#888" }}>
                      No hay tutores que coincidan con el filtro.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(editing)}
        onClose={closeEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Ficha de {editing?.name} {editing?.last_name}
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <DialogContent dividers>
            <Typography variant="body2" style={{ color: "#555" }}>
              Estos datos son de la persona, no de un cuatrimestre. El nombre,
              el mail y la capacidad se editan desde la tabla de tutores de cada
              cuatrimestre.
            </Typography>
            <TextField
              label="Teléfono"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
              margin="normal"
              inputProps={{ maxLength: 50 }}
              helperText="Dejalo vacío para borrarlo."
            />
            <TextField
              select
              label="Cargo"
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
              fullWidth
              margin="normal"
            >
              {POSITIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Departamento"
              value={form.department}
              onChange={(e) =>
                setForm({ ...form, department: e.target.value })
              }
              fullWidth
              margin="normal"
            >
              {DEPARTMENTS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditModal} variant="outlined" color="primary">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              Guardar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <MySnackbar
        open={notification.open}
        handleClose={handleSnackbarClose}
        message={notification.message}
        status={notification.status}
      />
    </Root>
  );
};

export default TutorDirectoryView;
